import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { subscribeAuth, getUserRole, getUserProfile } from '../../services/auth';
import { getUserPreferences, updateUserPreferences, updateUserUniversity, getSavedSearches, addSavedSearch, removeSavedSearch } from '../../services/users';
import { getAllListings, Listing, getListingById } from '../../services/listings';
import { subscribeToWalletTransactions, WalletTransaction, getWallet } from '../../services/wallet';
import { getBuyerOrders, Order } from '../../services/orders';
import CampusHeader from '../../components/feature/CampusHeader';
import Footer from '../../components/feature/Footer';
import { auth } from '../../lib/firebase';
import { useFavorites } from '../../hooks/useFavorites';
import { getCashbackSettings, type CashbackSettings } from '../../services/settings';
import { calculateCashbackForAmount } from '../../services/settings';
import { db } from '../../lib/firebase';
import { collection, doc, getDocs } from 'firebase/firestore';
import { subscribeActiveMessagesForUser, dismissMessage, type AdminMessage, recordBuyerOnboardingResponse } from '../../services/adminMessages';
import OnboardingTour from '../../components/OnboardingTour';

const STATIC_CATEGORIES: { id: string; name: string }[] = [
  { id: 'textbooks', name: 'Textbooks' },
  { id: 'electronics', name: 'Electronics' },
  { id: 'furniture', name: 'Furniture' },
  { id: 'clothing', name: 'Clothing' },
  { id: 'sport', name: 'Sport' },
  { id: 'event_ticket', name: 'Event Ticket' },
  { id: 'hostel', name: 'Hostel' },
];

function normalizeCat(raw: string): string {
  const s = (raw || '').toLowerCase().trim().replace(/\s+/g, '_');
  if (s === 'tickets' || s === 'event ticket' || s === 'event_ticket') return 'event_ticket';
  if (s === 'sport' || s === 'sports') return 'sport';
  if (s === 'books' || s === 'textbooks') return 'textbooks';
  if (s === 'electronics') return 'electronics';
  if (s === 'furniture') return 'furniture';
  if (s === 'clothing') return 'clothing';
  if (s === 'hostel') return 'hostel';
  return s;
}

export default function BuyerDashboardPage() {
  const navigate = useNavigate();
  const [uid, setUid] = useState<string | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [profile, setProfile] = useState<{ fullName: string | null; email: string | null; university?: string | null } | null>(null);

  const [prefs, setPrefs] = useState<{ preferredCategories: string[] }>({ preferredCategories: [] });
  const [allCategories, setAllCategories] = useState<{ id: string; name: string }[]>([]);
  const [listings, setListings] = useState<Listing[]>([]);
  const [filteredListings, setFilteredListings] = useState<Listing[]>([]);

  const [wallet, setWallet] = useState<{ balance: number; escrowBalance: number; totalEarnings: number } | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const [univDraft, setUnivDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [savedSearches, setSavedSearches] = useState<{ query: string; categories: string[] }[]>([]);
  const [onlyMyUniversity, setOnlyMyUniversity] = useState(false);

  const { favoriteItems } = useFavorites();

  // Map of orderId -> minimal product info for display
  const [purchaseProducts, setPurchaseProducts] = useState<Record<string, { title: string; image?: string; price?: number; listingId?: string }>>({});
  const [cashbackSettings, setCashbackSettings] = useState<CashbackSettings | null>(null);

  const [referralCount, setReferralCount] = useState(0);

  // Admin messages
  const [adminMsgs, setAdminMsgs] = useState<AdminMessage[]>([]);
  let adminUnsub: (() => void) | null = null;

  // Onboarding
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingSubmitting, setOnboardingSubmitting] = useState(false);

  useEffect(() => {
    const unsub = subscribeAuth(async (user) => {
      if (!user) {
        setCheckingAuth(false);
        navigate('/auth/sign-in');
        return;
      }
      setUid(user.uid);
      try {
        const { role } = await getUserRole(user.uid);
        if (role === 'seller') {
          navigate('/seller-dashboard');
          return;
        }
        // Subscribe to admin messages for buyer
        adminUnsub = subscribeActiveMessagesForUser(user.uid, 'buyer', (msgs) => {
          setAdminMsgs(msgs);
        });
        // Show onboarding on first visit
        const key = `buyer_onboarding_shown_${user.uid}`;
        if (!localStorage.getItem(key)) {
          setShowOnboarding(true);
          localStorage.setItem(key, '1');
        }
        const prof = await getUserProfile(user.uid);
        setProfile({ fullName: prof.fullName, email: prof.email, university: prof.university });
        const p = await getUserPreferences(user.uid);
        setPrefs(p);
        const wl = await getWallet(user.uid);
        setWallet(wl);
        const ss = await getSavedSearches(user.uid);
        setSavedSearches(ss);
        setLoadingOrders(true);
        const ords = await getBuyerOrders(user.uid);
        setOrders(ords);
        setLoadingOrders(false);
        // Load referral count
        try {
          const sub = collection(doc(db, 'users', user.uid), 'referrals');
          const snap = await getDocs(sub);
          setReferralCount(snap.size);
        } catch {}
      } finally {
        setCheckingAuth(false);
      }
    });
    return () => { unsub(); if (adminUnsub) adminUnsub(); };
  }, [navigate]);

  useEffect(() => {
    async function loadListings() {
      const items = await getAllListings();
      setListings(items);
      setAllCategories(STATIC_CATEGORIES);
    }
    loadListings();
  }, []);

  useEffect(() => {
    if (!uid) return;
    setLoadingTransactions(true);
    const unsub = subscribeToWalletTransactions(uid, (tx) => {
      setTransactions(tx);
      setLoadingTransactions(false);
    });
    return () => unsub();
  }, [uid]);

  useEffect(() => {
    const prefsNormalized = prefs.preferredCategories.map(normalizeCat);
    let filtered = listings.filter((l) => {
      const byCat = !prefsNormalized.length || prefsNormalized.includes(normalizeCat(l.category || ''));
      const q = searchQuery.trim().toLowerCase();
      const bySearch = !q || `${l.title || ''} ${l.description || ''}`.toLowerCase().includes(q);
      const byUniv = !onlyMyUniversity || !profile?.university ? true : (l.location || '').toLowerCase().includes(profile.university.toLowerCase());
      return byCat && bySearch && byUniv;
    });
    filtered.sort((a, b) => {
      const aTime = (a as any).createdAt?.seconds || 0;
      const bTime = (b as any).createdAt?.seconds || 0;
      return bTime - aTime;
    });
    setFilteredListings(filtered.slice(0, 12));
  }, [prefs, listings, searchQuery, onlyMyUniversity, profile]);

  const handleToggleCategory = async (catId: string) => {
    if (!uid) return;
    const currentNormalized = prefs.preferredCategories.map(normalizeCat);
    const exists = currentNormalized.includes(catId);
    const nextNormalized = exists ? currentNormalized.filter((c) => c !== catId) : [...currentNormalized, catId];
    const uniqueNext = Array.from(new Set(nextNormalized));
    await updateUserPreferences(uid, { preferredCategories: uniqueNext });
    setPrefs({ preferredCategories: uniqueNext });
  };

  const buyerName = useMemo(() => (profile?.fullName || auth.currentUser?.displayName || profile?.email || 'Buyer'), [profile]);

  useEffect(() => {
    const load = async () => {
      try {
        const s = await getCashbackSettings();
        setCashbackSettings(s);
      } catch (e) {
        console.error('Failed to load cashback settings', e);
      }
    };
    load();
  }, []);

  const saveUniversity = async () => {
    if (!uid || !univDraft.trim()) return;
    await updateUserUniversity(uid, univDraft.trim());
    setProfile((prev) => (prev ? { ...prev, university: univDraft.trim() } : prev));
    setUnivDraft('');
  };

  const handleSaveSearch = async () => {
    if (!uid) return;
    const normalizedCats = prefs.preferredCategories.map(normalizeCat);
    const payload = { query: searchQuery.trim(), categories: normalizedCats };
    await addSavedSearch(uid, payload);
    const ss = await getSavedSearches(uid);
    setSavedSearches(ss);
  };

  const applySavedSearch = async (s: { query: string; categories: string[] }) => {
    setSearchQuery(s.query || '');
    setPrefs({ preferredCategories: s.categories || [] });
  };

  const removeSaved = async (s: { query: string; categories: string[] }) => {
    if (!uid) return;
    await removeSavedSearch(uid, s);
    const ss = await getSavedSearches(uid);
    setSavedSearches(ss);
  };

  // Build a simple purchases view using orders; fallback to wallet tx with orderId
  const recentPurchases = useMemo(() => {
    if (orders && orders.length) return orders.slice(0, 6);
    const txOrders = (transactions || []).filter(t => t.orderId || /purchase|escrow/i.test(t.description || '')).slice(0, 6);
    return txOrders.map(t => ({ id: t.orderId || t.id, buyerId: uid || '', sellerId: '', status: (t.status === 'held' ? 'payment_held' : 'completed') as Order['status'], totalAmount: t.amount, createdAt: t.createdAt })) as Order[];
  }, [orders, transactions, uid]);

  // Load product details for each order for thumbnail/title
  useEffect(() => {
    async function hydrate() {
      const map: Record<string, { title: string; image?: string; price?: number; listingId?: string }> = {};
      const tasks: Promise<void>[] = [];
      recentPurchases.forEach((o) => {
        const key = o.id || o.orderId || '';
        if (!key) return;
        const item = o.orderItems?.[0];
        if (item?.title || item?.image) {
          map[key] = { title: item.title || 'Product', image: item.image, price: item.price, listingId: item.listingId };
        } else if (item?.listingId) {
          tasks.push((async () => {
            const listing = await getListingById(item.listingId!);
            if (listing) map[key] = { title: listing.title, image: listing.images?.[0] || listing.image, price: listing.price, listingId: listing.id };
          })());
        }
      });
      if (tasks.length) await Promise.all(tasks);
      setPurchaseProducts(map);
    }
    hydrate();
  }, [recentPurchases]);

  const renderSkeletonRows = (count: number) => (
    <ul className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="flex items-center justify-between border rounded p-3 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-200 rounded" />
            <div>
              <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
              <div className="h-3 w-40 bg-gray-200 rounded" />
            </div>
          </div>
          <div className="text-right">
            <div className="h-4 w-16 bg-gray-200 rounded mb-2" />
            <div className="h-3 w-24 bg-gray-200 rounded" />
          </div>
        </li>
      ))}
    </ul>
  );

  if (checkingAuth) {
    return <div className="min-h-screen flex items-center justify-center"><p>Loading…</p></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CampusHeader />
      <main className="max-w-6xl mx-auto px-4 py-6 md:py-8">
        {showOnboarding && (
          <OnboardingTour
            role="buyer"
            onClose={async () => {
              setOnboardingSubmitting(true);
              try {
                if (auth.currentUser) {
                  // Optionally persist server-side marker
                  await recordBuyerOnboardingResponse(auth.currentUser.uid, { seen: true, seenAt: Date.now() });
                }
              } catch {}
              setOnboardingSubmitting(false);
              setShowOnboarding(false);
            }}
          />
        )}
        {adminMsgs.length > 0 && adminMsgs.map((m) => (
          <div key={m.id} className="mb-4 rounded-lg border border-indigo-200 bg-indigo-50 p-3 flex items-start justify-between">
            <div className="flex items-start gap-2">
              <i className="ri-megaphone-line text-indigo-600 text-lg"></i>
              <p className="text-sm text-indigo-800">{m.text}</p>
            </div>
            <button
              className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
              onClick={async () => {
                if (!auth.currentUser || !m.id) return;
                try {
                  await dismissMessage(m.id, auth.currentUser.uid);
                  setAdminMsgs(prev => prev.filter(x => x.id !== m.id));
                } catch {}
              }}
            >
              Dismiss
            </button>
          </div>
        ))}
        {cashbackSettings?.enabled && (
          <div className="mb-6 rounded-xl border border-red-200 bg-gradient-to-r from-orange-50 to-red-50 p-4 md:p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200">Limited Time</span>
                <h2 className="text-base md:text-lg font-bold text-gray-900">Cashback Offer</h2>
              </div>
              <i className="ri-fire-line text-red-500 text-xl"></i>
            </div>
            {cashbackSettings.message && (
              <p className="mt-2 text-sm text-gray-700">{cashbackSettings.message}</p>
            )}
            <div className="mt-3 flex items-center gap-3 text-sm">
              <i className="ri-money-cny-box-line text-red-500"></i>
              <span className="text-gray-800">Earn cashback on eligible purchases today.</span>
            </div>
          </div>
        )}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Welcome, {buyerName}</h1>
              {profile?.university ? (
                <p className="text-gray-600">{profile.university}</p>
              ) : (
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="text"
                    value={univDraft}
                    onChange={(e) => setUnivDraft(e.target.value)}
                    placeholder="Enter your University"
                    className="px-3 py-1 border border-gray-300 rounded"
                  />
                  <button
                    onClick={saveUniversity}
                    className="px-3 py-1 bg-teal-600 text-white rounded hover:bg-teal-700"
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/sell?step=verification')}
                className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 whitespace-nowrap"
              >
                Become a Seller
              </button>
              {wallet && (
                <div className="flex gap-6">
                  <div>
                    <p className="text-xs text-gray-500">Wallet Balance</p>
                    <p className="text-xl font-semibold text-teal-600">₦{wallet.balance.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Held in Escrow</p>
                    <p className="text-xl font-semibold text-orange-600">₦{wallet.escrowBalance.toLocaleString()}</p>
                  </div>
                  {cashbackSettings?.enabled && (
                    <div>
                      <p className="text-xs text-gray-500">Cashback Earned</p>
                      <p className="text-xl font-semibold text-green-600">₦{Math.round(totalCashbackEarned).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              )}
              <Link to="/referrals" className="flex items-center gap-2 px-3 py-2 border rounded hover:bg-gray-50">
                <span className="text-xl">🎁</span>
                <div>
                  <p className="text-xs text-gray-500">Referrals</p>
                  <p className="text-base font-semibold text-gray-800">{referralCount}</p>
                </div>
              </Link>
            </div>
        </div>
      </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
              {loadingTransactions ? (
                renderSkeletonRows(4)
              ) : transactions.length === 0 ? (
                <p className="text-gray-500">No recent transactions</p>
              ) : (
                <ul className="space-y-3">
                  {transactions.slice(0, 8).map(tx => (
                    <li key={tx.id} className="flex items-center justify-between border rounded p-3">
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded text-xs ${tx.type === 'credit' ? 'bg-green-100 text-green-700' : tx.type === 'debit' ? 'bg-red-100 text-red-700' : tx.type === 'escrow_hold' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'}`}>{tx.type}</span>
                        <span className="text-gray-700">{tx.description}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">₦{(tx.amount || 0).toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{tx.createdAt?.toDate ? tx.createdAt.toDate().toLocaleString() : ''}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Latest Listings */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Your Liked Products</h2>
                <p className="text-sm text-gray-500">Shows items you marked as favorite</p>
              </div>
              {/* Removed search and university filters to focus on favorites-only feed */}
              {favoriteItems.length === 0 ? (
                <div className="text-gray-500">
                  <p>No favorites yet. Browse the marketplace to add items.</p>
                  <Link to="/marketplace" className="text-teal-600 hover:text-teal-700">Browse Marketplace</Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {favoriteItems.map((item: FavoriteGridItem) => (
                    <div key={item.id} className="border rounded-lg overflow-hidden bg-white">
                      {item.image && (
                        <img src={item.image} alt={item.name} className="w-full h-32 object-cover" />
                      )}
                      <div className="p-3">
                        <h3 className="font-medium text-gray-900 truncate">{item.name}</h3>
                        <p className="text-teal-600 font-semibold">₦{Number(item.price || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Preferences */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Preferences</h2>
              <div className="flex flex-wrap gap-2">
                {allCategories.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleToggleCategory(c.id)}
                    className={`px-3 py-1 rounded border ${prefs.preferredCategories.map(normalizeCat).includes(c.id) ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300'}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Saved Searches */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Saved Searches</h2>
              {savedSearches.length === 0 ? (
                <p className="text-gray-500">No saved searches yet</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {savedSearches.map((s, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-1 border rounded">
                      <button onClick={() => applySavedSearch(s)} className="text-sm text-gray-700">{s.query || 'Untitled'} ({s.categories.length || 0} cats)</button>
                      <button onClick={() => removeSaved(s)} className="text-xs text-red-600">Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Purchases */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Purchases</h2>
              {recentPurchases.length === 0 ? (
                <p className="text-gray-500">No purchases yet</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {recentPurchases.map((o) => (
                    <div key={o.id} className="border rounded-lg p-3">
                      <p className="text-sm text-gray-700">Order ID: {o.id}</p>
                      <p className="text-sm text-gray-700">Status: {o.status}</p>
                      <p className="text-sm text-gray-700">Total: ₦{(o.totalAmount || 0).toLocaleString()}</p>
                      {cashbackSettings?.enabled && (
                        <p className="text-sm text-green-700">Cashback: ₦{Math.round(cashbackByOrder[o.id || ''] || 0).toLocaleString()}</p>
                      )}
                      {purchaseProducts[o.id || '']?.image && (
                        <img src={purchaseProducts[o.id || '']?.image} alt={purchaseProducts[o.id || '']?.title || 'Product'} className="w-full h-24 object-cover rounded mt-2" />
                      )}
                      <p className="text-sm text-gray-700 mt-1">{purchaseProducts[o.id || '']?.title || 'Product'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Favorites Panel */}
          <div className="space-y-4 md:space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Favorites</h2>
              {favoriteItems.length === 0 ? (
                <p className="text-gray-500">No favorites yet</p>
              ) : (
                <ul className="space-y-3">
                  {favoriteItems.map((fav) => (
                    <li key={fav.id} className="flex items-center gap-3">
                      <img src={fav.image || ''} alt={fav.name} className="w-12 h-12 rounded object-cover" />
                      <div>
                        <p className="font-medium text-gray-900">{fav.name}</p>
                        <p className="text-sm text-gray-600">₦{Number(fav.price || 0).toLocaleString()}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Tips</h2>
              <ul className="list-disc pl-5 text-xs md:text-sm text-gray-700 space-y-2">
                <li>Use saved searches to get alerts for new items</li>
                <li>Set your university to filter by local listings</li>
                <li>Explore categories to personalize your feed</li>
              </ul>
            </div>
          </div>
        </div>
        {showOnboarding && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex sm:items-center items-start justify-center p-4 sm:p-6" onClick={() => setShowOnboarding(false)}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm sm:max-w-md p-4 sm:p-6 max-h-[85vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-user-3-line text-teal-600 text-2xl"></i>
                <h3 className="text-lg font-semibold text-gray-900">Welcome! Quick setup</h3>
              </div>
              <p className="text-sm text-gray-700 mb-4">Are you primarily buying or would you like to sell as well?</p>
              <div className="flex flex-col gap-2">
                <button
                  className="w-full px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700"
                  disabled={onboardingSubmitting}
                  onClick={async ()=>{
                    if (!auth.currentUser) { setShowOnboarding(false); return; }
                    try {
                      setOnboardingSubmitting(true);
                      await recordBuyerOnboardingResponse(auth.currentUser.uid, { choice: 'buyer', ts: Date.now() });
                      setShowOnboarding(false);
                    } finally { setOnboardingSubmitting(false); }
                  }}
                >
                  Continue as Buyer
                </button>
                <button
                  className="w-full px-4 py-2 border rounded hover:bg-gray-50"
                  disabled={onboardingSubmitting}
                  onClick={async ()=>{
                    if (!auth.currentUser) { navigate('/sell'); return; }
                    try {
                      setOnboardingSubmitting(true);
                      await recordBuyerOnboardingResponse(auth.currentUser.uid, { choice: 'seller', ts: Date.now() });
                      navigate('/sell');
                    } finally { setOnboardingSubmitting(false); }
                  }}
                >
                  I want to Sell
                </button>
              </div>
              <button
                className="mt-4 text-xs text-gray-500 hover:text-gray-700"
                onClick={async ()=>{
                  if (auth.currentUser) {
                    try { await recordBuyerOnboardingResponse(auth.currentUser.uid, { choice: 'dismiss', ts: Date.now() }); } catch {}
                  }
                  setShowOnboarding(false);
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}